export interface BridgeTemplate {
  id: string
  eyebrow: string
  headlineLead: string
  headlineHighlight: string
  headlineTrail: string
  subhead: string
  stepLabel: string
  cardTitle: string
  cardSubtitle: string
  ctaLabel: string
  // Optional: form variant. 'email' (default) shows the email capture form.
  // 'cta' shows a simple call-to-action card with no email field.
  variant?: 'email' | 'cta' | 'selector'
  // Optional copy used by the 'cta' variant
  cardHeadline?: string
  cardSubcopy?: string
  cardFootnote?: string
  testimonials: {
    initial: string
    quote: string
    name: string
    school: string
  }[]
  stats: {
    value: string
    label: string
  }[]
}

export const bridgeTemplates: Record<string, BridgeTemplate> = {
  v1: {
    id: 'v1',
    eyebrow: 'FINAL STEP — CONFIRM YOUR MATCH',
    headlineLead: 'Your',
    headlineHighlight: 'Free Program Matches',
    headlineTrail: 'Are Ready to View',
    subhead:
      'Thousands of accredited online programs are waiting. Confirm your email to unlock your personalized results — it takes 10 seconds.',
    stepLabel: 'STEP 2 OF 2 · ALMOST DONE',
    cardTitle: 'Confirm Your Email Address',
    cardSubtitle:
      "We'll send your matched programs here. No spam — just your results.",
    ctaLabel: 'View My Program Matches',
    testimonials: [
      {
        initial: 'M',
        quote:
          'I found an accredited online MBA in under 5 minutes. The matching process was incredibly easy.',
        name: 'Maria T.',
        school: 'Enrolled at SNHU',
      },
      {
        initial: 'D',
        quote:
          'Was skeptical at first, but they matched me with exactly the criminal justice program I needed.',
        name: 'Derek L.',
        school: 'Enrolled at Liberty University Online',
      },
    ],
    stats: [
      {
        value: '200+',
        label: 'Accredited Schools',
      },
      {
        value: '1,400+',
        label: 'Online Programs',
      },
      {
        value: '100%',
        label: 'Free Matching',
      },
    ],
  },
  v2: {
    id: 'v2',
    eyebrow: 'NEXT SESSION STARTS SOON',
    headlineLead: 'Earn an',
    headlineHighlight: 'Online Degree',
    headlineTrail: 'On Your Schedule',
    subhead:
      'Flexible, accredited online programs designed for working adults. Explore your options and take the first step toward your future today.',
    stepLabel: 'GET STARTED',
    cardTitle: 'Earn an Online Degree',
    cardSubtitle: 'Next session starts soon.',
    ctaLabel: 'Get Started',
    variant: 'cta',
    cardHeadline: 'Earn an Online Degree',
    cardSubcopy: 'Next session starts soon.',
    cardFootnote:
      'Want to learn more? Our admissions team is here to support you and get your questions answered.',
    testimonials: [
      {
        initial: 'J',
        quote:
          'The online format gave me the flexibility to keep working full-time while earning my degree. Best decision I ever made.',
        name: 'Jasmine R.',
        school: "Bachelor's in Business Administration",
      },
      {
        initial: 'A',
        quote:
          'Supportive instructors and a curriculum that actually applies to my career. I finished in under three years.',
        name: 'Andre P.',
        school: "Master's in IT Management",
      },
    ],
    stats: [
      { value: '200+', label: 'Accredited Schools' },
      { value: '1,400+', label: 'Online Programs' },
      { value: '100%', label: 'Free Matching' },
    ],
  },
}

// v3, v4, v5 mirror v2's CTA content. Created as separate IDs so they can
// diverge later (e.g. for A/B test variants) without code changes.
// v5's click behavior routes the user to the /colleges page.
bridgeTemplates.v3 = { ...bridgeTemplates.v2, id: 'v3' }
bridgeTemplates.v4 = { ...bridgeTemplates.v2, id: 'v4' }
bridgeTemplates.v5 = { ...bridgeTemplates.v2, id: 'v5' }
// v7 mirrors v6 (selector variant), but redirects externally to media-matchers
bridgeTemplates.v7 = null as unknown as BridgeTemplate // populated below after v6

// v6: dropdown selector variant — user picks degree level, area, and program,
// then is sent to the listings page with those filters prepopped via URL params.
bridgeTemplates.v6 = {
  id: 'v6',
  eyebrow: 'FIND YOUR PROGRAM',
  headlineLead: 'Get Started in',
  headlineHighlight: '60 Seconds',
  headlineTrail: '',
  subhead:
    "Tell us a little about your educational goals and we'll match you with accredited online programs that fit.",
  stepLabel: 'STEP 1 OF 1',
  cardTitle: 'Get Started in 60 Seconds',
  cardSubtitle: 'Tell us about your educational goals',
  ctaLabel: 'Continue to Next Step',
  variant: 'selector',
  testimonials: [
    {
      initial: 'M',
      quote:
        'I found an accredited online MBA in under 5 minutes. The matching process was incredibly easy.',
      name: 'Maria T.',
      school: 'Enrolled at SNHU',
    },
    {
      initial: 'D',
      quote:
        'Was skeptical at first, but they matched me with exactly the criminal justice program I needed.',
      name: 'Derek L.',
      school: 'Enrolled at Liberty University Online',
    },
  ],
  stats: [
    { value: '200+', label: 'Accredited Schools' },
    { value: '1,400+', label: 'Online Programs' },
    { value: '100%', label: 'Free Matching' },
  ],
}

// v7: same selector UI as v6, different submission target (external API redirect)
bridgeTemplates.v7 = { ...bridgeTemplates.v6, id: 'v7' }

// v8: same selector UI as v6/v7, but on submit fires the Clicks Search
// API in-page and renders results as a one-at-a-time carousel (same UX
// as the post-form step 8 in the request-information flow).
bridgeTemplates.v8 = { ...bridgeTemplates.v6, id: 'v8' }

export function getBridgeTemplate(id: string | undefined): BridgeTemplate {
  if (!id) return bridgeTemplates.v1
  return bridgeTemplates[id] || bridgeTemplates.v1
}
